import * as system from 'durandal/system';
import * as router from 'plugins/router';

import titleMessageModal from '@/legacy/modals/styled/titleMessage';
import * as companyService from '@/legacy/services/company';
import * as bankService from '@/legacy/services/company/bank';
import * as banksInfoService from '@/legacy/services/info/banks';
import { SessionTimeoutService } from '@/legacy/services/sessionTimeout';
import * as userService from '@/legacy/services/user';
import * as xhrService from '@/legacy/services/xhrService';
import * as plootoUtils from '@/legacy/utils/plooto';
import * as viewModels from '@/legacy/viewmodels/viewModels';

import { BankAccountAddModel } from '../../viewmodels/addModels';

const API_PREFIX = 'v1';

export class SessionTokenViewModel {
  public tokenSet: boolean;

  public sessionToken: string;
}
export class BankLoginRequirementsBindingModel {
  public bankIdentifier: number;

  public sessionToken: string;

  public country: string;
}

export class BankLoginRequirementsViewModel {
  public name: string;

  public logo: string;

  public baseUrl: string;

  public loginForm: CredentialsLoginForm;
}
export class CredentialsLoginForm {
  public forgetPasswordUrl: string;

  public formType: string;

  public loginRow: Array<LoginRow>;
}
export class LoginRow {
  public label: string;

  public fields: Array<LoginField>;
}
export class LoginField {
  public id: number;

  public name: string;
}

export class PostSubmitRequirementsBindingModel {
  public bankIdentifier: number;

  public country: string;

  public sessionToken: string;

  public fields: Array<RequirementsField>;
}
export class RequirementsField {
  public fieldId: number;

  public fieldValue: string;
}

export class PostSubmitRequirementsViewModel {
  public accountLinkId: number;

  public aggregationSource: string;

  public loginForm: AdditionalLoginViewModel;
}
export class AdditionalLoginViewModel {
  public mfaTimeout: number;

  public formType: string;

  public questions: Array<MfaQuestion>;
}
export class MfaQuestion {
  public label: string;

  public mfaQuestionFields: Array<MfaQuestionField>;
}
export class MfaQuestionField {
  public id: string;

  public name: string;

  public type: string;

  public imageBase64: string;
}

export class UpdateAccountLinkBindingModel {
  public accountLinkid: number;

  public formType: string;

  public answer: Array<MfaAnswerRow>;

  public sessionToken: string;

  public institution: number;
}
export class MfaAnswerRow {
  public label: string;

  public fields: Array<MfaAnswerField>;
}
export class MfaAnswerField {
  public id: string;

  public value: string;
}

export class UpdateAccountLinkViewModel {
  public success: boolean;
}

export class GetLinkStatusAndAccountsBindingModel {
  public accountLinkId: number;

  public sessionToken: string;

  public country: string;
}

export class GetLinkStatusAndAccountsViewModel {
  public status: string;

  public statusMessage: string;

  public storageToken: string;

  public loginForm: AdditionalLoginViewModel;

  public accounts: Array<LinkedAccount>;
}
export class LinkedAccount {
  public viewId: string;

  public accountNumber: string;

  public accountName: string;

  public balance: LinkedAccountBalance;

  public accountType: string;

  public bankTransferCode: Array<BankTransferCode>;

  public incompleteData: boolean;

  public availableBalance: viewModels.Money;

  public hasHolderProfile: boolean;
}
export class LinkedAccountBalance {
  public amount: number;

  public currency: string;
}
export class BankTransferCode {
  public bankId: string;
}

export class PostAddAccountToPlootoBindingModel {
  public viewId: string;

  public storageToken: string;

  public country: string;
}

export class PostAddAccountToPlootoViewModel {
  public name: string;

  public bank: string;
}

export class SelectedAccountBindingModel {
  public sessionToken: string;

  public storageToken: string;

  public selectedBankAccountId: string;

  public accountName: string;

  public country: string;

  public hasHolderProfile: boolean;
}

export class BankLoginAuthSessionBindingModel {
  public companyId: string;

  public country: string;

  public bankIdentifier: number;

  public bankLoginFormType: string;

  public bankLoginRelatedId: string;
}

export class BankLoginSearchResultViewModel {
  public name: string;

  public providerId: number;
}

export interface HandleAddBankAccountErrorParams {
  error: any;
  forceManualEntryCallback?: (errorText: string) => void;
}

export class BankLoginService {
  private companyService: companyService.CompanyService;

  private userService: userService.UserService;

  public loginStatus: string;

  constructor() {
    this.companyService = companyService.Instance;
    this.userService = userService.Instance;
  }

  public getSession(
    bankLoginAuthSessionBindingModel: BankLoginAuthSessionBindingModel
  ): JQueryPromise<SessionTokenViewModel> {
    const requestUrl = `${
      import.meta.env.APP_URLS_API_URL
    }/${API_PREFIX}/bankLogin/payee/authSession`;

    return system
      .defer<SessionTokenViewModel>((dfd) => {
        this.userService
          .AuthorizedGetFromUri(requestUrl, bankLoginAuthSessionBindingModel)
          .done((sessionData) => {
            if (plootoUtils.IsNullOrUndefined(sessionData) || sessionData.error === true) {
              dfd.reject(sessionData);
              return;
            }
            dfd.resolve(sessionData);
          })
          .fail(() => {
            dfd.reject();
          });
      })
      .promise();
  }

  public getBankRequirements(
    params: BankLoginRequirementsBindingModel
  ): JQueryPromise<BankLoginRequirementsViewModel> {
    const requestUrl = `${import.meta.env.APP_URLS_API_URL}/${API_PREFIX}/bankLogin/payee/${
      params.sessionToken
    }/requirements/${params.bankIdentifier}`;

    return system
      .defer<BankLoginRequirementsViewModel>((dfd) => {
        this.userService
          .AuthorizedGet(requestUrl)
          .done((requirementsData) => {
            if (
              plootoUtils.IsNullOrUndefined(requirementsData) ||
              requirementsData.error === true
            ) {
              dfd.reject(requirementsData);
              return;
            }
            dfd.resolve(requirementsData);
          })
          .fail(() => {
            dfd.reject();
          });
      })
      .promise();
  }

  public postSubmitRequirements(
    params: PostSubmitRequirementsBindingModel
  ): JQueryPromise<PostSubmitRequirementsViewModel> {
    const requestUrl = `${import.meta.env.APP_URLS_API_URL}/${API_PREFIX}/bankLogin/payee/${
      params.sessionToken
    }/credentials`;

    return system
      .defer<PostSubmitRequirementsViewModel>((dfd) => {
        this.userService
          .AuthorizedPost(requestUrl, params)
          .done((submitResult) => {
            if (plootoUtils.IsNullOrUndefined(submitResult) || submitResult.error === true) {
              dfd.reject(submitResult);
              return;
            }
            dfd.resolve(submitResult);
          })
          .fail(() => {
            dfd.reject();
          });
      })
      .promise();
  }

  public getLoginStatus(): string {
    return this.loginStatus;
  }

  public postSubmitAccountLinkUpdate(
    params: UpdateAccountLinkBindingModel
  ): JQueryPromise<PostSubmitRequirementsViewModel> {
    const requestUrl = `${import.meta.env.APP_URLS_API_URL}/${API_PREFIX}/bankLogin/payee/${
      params.sessionToken
    }/updateLinkAccount`;

    return system
      .defer<PostSubmitRequirementsViewModel>((dfd) => {
        this.userService
          .AuthorizedPut(requestUrl, params)
          .done((updateResult) => {
            if (plootoUtils.IsNullOrUndefined(updateResult) || updateResult.error === true) {
              dfd.reject(updateResult);
              return;
            }
            dfd.resolve(updateResult);
          })
          .fail(() => {
            dfd.reject();
          });
      })
      .promise();
  }

  public getStatusOrAccounts(
    params: GetLinkStatusAndAccountsBindingModel
  ): JQueryPromise<GetLinkStatusAndAccountsViewModel> {
    const requestUrl = `${import.meta.env.APP_URLS_API_URL}/${API_PREFIX}/bankLogin/payee/${
      params.sessionToken
    }/getLinkStatusAndGetAccounts/${params.country}/${params.accountLinkId}`;

    return system
      .defer<GetLinkStatusAndAccountsViewModel>((dfd) => {
        this.userService
          .AuthorizedGet(requestUrl)
          .done((statusResult) => {
            if (plootoUtils.IsNullOrUndefined(statusResult) || statusResult.error === true) {
              dfd.reject(statusResult);
              return;
            }
            dfd.resolve(statusResult);
          })
          .fail(() => {
            dfd.reject();
          });
      })
      .promise();
  }

  public getBankSearchResultsWithXhr(searchQuery: string, country: string) {
    const dataPromiseWithXhr = new xhrService.PromiseWithXhr<
      Array<BankLoginSearchResultViewModel>
    >();

    dataPromiseWithXhr.getXhr = () =>
      this.userService.AuthorizedGetFromUri(
        `${import.meta.env.APP_URLS_API_URL}/${API_PREFIX}/bankLogin/payee/providersSearch`,
        { searchQuery, country }
      ) as JQueryXHR;

    xhrService.Instance.initiatePromiseWithXhr(dataPromiseWithXhr); // handles both .errors and logout errors

    return dataPromiseWithXhr;
  }

  public async addBankAccount(
    selectedAccount: SelectedAccountBindingModel,
    bankMetaInfo: banksInfoService.BankInfoMetadata
  ): Promise<bankService.BankAccount> {
    const bankAccountAddModel = new BankAccountAddModel();

    bankAccountAddModel.bank = bankMetaInfo.bankName;
    bankAccountAddModel.name = selectedAccount.accountName;

    bankAccountAddModel.accountDetails = selectedAccount as any;

    const newBankAccount = await bankService.Instance.CreateAccount(bankAccountAddModel);

    return newBankAccount;
  }

  public handleAddBankAccountError({
    error,
    forceManualEntryCallback,
  }: HandleAddBankAccountErrorParams): any {
    const getErrorDetails = () => {
      if (error && error.type) {
        switch (error.type) {
          case 'invalid.authentication.relatedId': // This authentication is specific to Flinks that will bypass AuthorizePost
            // We will need to handle session timeouts here as AuthorizedGetFromUri doesn't handle it
            SessionTimeoutService.showTimeout();
            return { closeDialog: true };
          case 'invalid.parameter':
            // This would only happen if transit, routing, account number fields are missing
            // This should never happen as we validate banking details
            return { closeDialog: true };

          case 'account.used':
            return {
              dialog: {
                title: 'Bank account in use',
                message:
                  'This bank account is already associated with a Plooto account. You could try a different bank account or contact Plooto Support for further assistance.',
              },
              redirect: 'user/bank',
            };
          case 'blacklisted.account':
            return {
              dialog: {
                title: 'Account Error',
                message:
                  'This account cannot be created at this time. Please contact compliance@plooto.com.',
              },
            };
          case 'invalid.accountId':
            return {
              dialog: {
                title: 'Account in use',
                message: 'This account is already activated.',
              },
            };
          case 'invalid.isCompanyVerificationMandatory':
            return {
              dialog: {
                title: 'Company Verification Pending',
                message:
                  'Your company verification must be approved by Plooto Compliance Department before you can add your company bank account. Please allow 1-2 business days for Plooto Compliance Department to review your company documents upon upload.',
              },
              redirect: 'user/company/compliance/dialog/validateCompany',
            };
          case 'identityVerification.required':
            return {
              dialog: {
                title: 'Identity Verification Outstanding',
                message:
                  'Please complete your identity verification before adding this bank account.',
              },
              redirect: 'user/identityVerification',
            };

          case 'failed.generateAuthorizationToken': // From GenerateFlinksAuthorizationToken()
          case 'invalid.secondaryBankResponse': // From HandleFlinksFailedResponse()
          case 'secondaryBank.request.unauthorized':
          case 'secondaryBank.request.bad.response':
          case 'secondaryBank.mfa.required':
          case 'secondaryBank.failed.response':
          case 'secondaryBank.response.none':
          case 'provider.timeout': {
            const message =
              'Our instant bank validation is currently unavailable. Please use a void check validation option instead. Or you can try bank login option again at a later time.';
            return {
              dialog: {
                title: 'Instant Bank Validation Unavailable',
                message,
              },
              fallbackToManualEntry: message,
            };
          }
          case 'companyCapabilities':
            return {
              fallbackToManualEntry:
                'This company must complete outstanding verification documents before using bank login functionality. In the mean time, you can try using the void check option below for testing purposes.',
            };
          case 'rateLimit.authSession':
            return {
              fallbackToManualEntry:
                'Having trouble selecting your bank account? Please use a void check validation option instead. Or you can try bank login option again tomorrow.',
            };

          // From ValidateFlinksBankAccount()
          case 'addAccount.notSufficientInfo':
          case 'addAccount.failed': {
            const message =
              'The selected account did not return certain information that is mandatory in order to be used by Plooto. Please verify your account by entering your cheque details instead.';
            return {
              dialog: {
                title: 'Insufficient Banking Details',
                message,
              },
              fallbackToManualEntry: message,
            };
          }

          default: {
            const message = `Having trouble selecting your bank account? Please use a void check validation option instead. Or you can try bank login option again at a later time. (${error.type})`;
            return {
              dialog: {
                title: 'Error Adding Banking',
                message,
              },
              fallbackToManualEntry: message,
            };
          }
        }
      }

      return { closeDialog: true }; // For errors without error.type
    };

    const errorDetails = getErrorDetails();

    if (errorDetails.dialog) {
      const { title, message } = errorDetails.dialog;
      titleMessageModal.show({
        title,
        message,
        svgIconSymbolLarge: 'svg-warning-large',
      } as any);
    }

    if (errorDetails.redirect) {
      router.navigate(errorDetails.redirect);
    }

    // It is important to understand that we'll redirect to void check even if reason is null (It just won't show the second message area)
    if (errorDetails.fallbackToManualEntry !== undefined) {
      forceManualEntryCallback(errorDetails.fallbackToManualEntry);
    }

    return errorDetails;
  }
}

export const Instance = new BankLoginService();
